import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getStudentRemarks, deleteStudentRemark } from './api/studentScreensApi'

const PAGE_SIZES = [10, 25, 50, 100]

export default function StudentRemarksScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')
  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pageSize, setPageSize] = useState(25)
  const [entries, setEntries] = useState([])

  useEffect(() => { loadStudent(); loadRemarks() }, [studentId])

  const loadStudent = async () => {
    setIsLoading(true)
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({ id: studentId, firstName: 'Student', lastName: '' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadRemarks = async () => {
    try {
      const rows = await getStudentRemarks(studentId)
      setEntries(rows || [])
    } catch {
      setEntries([])
    }
  }

  const handleDelete = async (remarkId) => {
    try {
      await deleteStudentRemark(studentId, remarkId)
      await loadRemarks()
    } catch {
      // ignore
    }
  }

  const pagedEntries = Array.isArray(entries) ? entries.slice(0, pageSize) : []

  const goBack = () => navigate('/admission/students/list')
  const goPrint = () => window.print()
  const name = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Remarks</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">Remarks — {name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={goPrint} className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-container-high text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface" title="Print">
            <span className="material-symbols-outlined text-[20px]">print</span>
          </button>
          <button type="button" onClick={goBack} className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-container-high text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface" title="Back">
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          </button>
        </div>
      </section>

      <div className="flex items-center gap-3 rounded-2xl bg-primary px-6 py-4">
        <span className="material-symbols-outlined text-on-primary text-[22px]">sticky_note_2</span>
        <h2 className="text-base font-bold text-on-primary">Remarks for {name}</h2>
      </div>

      <div className="flex items-center justify-end gap-2">
        <span className="text-xs font-bold text-on-surface-variant">Show</span>
        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="rounded-lg border border-outline-variant/30 bg-white px-3 py-1.5 text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-primary/20">
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-xs font-bold text-on-surface-variant">entries</span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/30 bg-surface-container-low">
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[140px]">Date</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Remark</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Particulars</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[160px]">By</th>
              <th className="px-5 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant w-[90px] text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center">
                  <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
                  <p className="mt-3 text-sm text-on-surface-variant">Loading remarks...</p>
                </td>
              </tr>
            ) : pagedEntries.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-12">
                  <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8">
                    <p className="text-center text-sm font-bold leading-relaxed text-blue-600">
                      No entries meet selected criteria — Either there are no entries in the selected module or you have selected invalid filter conditions.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              pagedEntries.map((entry, idx) => (
                <tr key={entry.id} className={`border-b border-outline-variant/10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-primary/5 transition-colors`}>
                  <td className="px-5 py-3.5 text-sm text-on-surface font-mono whitespace-nowrap">{entry.date}</td>
                  <td className="px-5 py-3.5 text-sm text-on-surface leading-relaxed">{entry.remark}</td>
                  <td className="px-5 py-3.5 text-sm text-on-surface-variant leading-relaxed">{entry.particulars}</td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-container-high px-3 py-1 text-xs font-bold text-on-surface">
                      <span className="material-symbols-outlined text-[14px] text-on-surface-variant">person</span>
                      {entry.by}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="inline-flex items-center gap-1 rounded-lg bg-error/10 px-2.5 py-1.5 text-xs font-bold text-error hover:bg-error/20 transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm">delete</span>
                      <span>Delete</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on <span className="font-semibold text-on-surface">{student.createdDate}</span></span>
          )}
          {student?.updatedBy && (
            <span>Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on <span className="font-semibold text-on-surface">{student.updatedDate}</span></span>
          )}
        </div>
      </section>
    </div>
  )
}
